import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconLock, IconChevronRight, IconAlertTriangle } from '@tabler/icons-react';
import { fetchCheckpoints } from '../features/checkpointsSlice';
import { selectUnitId, selectUnits, setUnitId, selectSelectedUnitIds, selectUnitsLedByUser } from '../features/unitsSlice';
import { selectUserData } from '../features/userSlice';

import MeasureStatusRow from '../components/MeasureStatusRow';
import ActionAreaStatusRow from '../components/ActionAreaStatusRow';
import QuestionStatusRow from '../components/QuestionStatusRow';
import UnitFilterContainer from '../components/filter/UnitFilterContainer';
import './Checkpoints.css';

/**
 * Determines the status category of a checkpoint based on its data
 * @param {Object} checkpoint - The checkpoint object
 * @returns {string} - 'overdue' | 'upcoming' | 'completed'
 */
const getCheckpointStatus = (checkpoint) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDate = new Date(checkpoint.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    // Check if checkpoint is completed based on type
    const isCompleted = () => {
        if (checkpoint.checkpointType === 'questions') {
            // Questions: completed if ALL questions have any answer (ja or nein)
            if (!checkpoint.questions || checkpoint.questions.length === 0) return false;
            return checkpoint.questions.every(q => q.status === 'ja' || q.status === 'nein');
        }
        
        if (checkpoint.checkpointType === 'measures') {
            // Measures: completed if ALL measures have a valid status (not null, empty, or 'nicht begonnen')
            // If no measures exist yet, checkpoint cannot be completed
            if (!checkpoint.actionAreas || checkpoint.actionAreas.length === 0) return false;
            
            // Check if there are any measures at all
            const allMeasures = checkpoint.actionAreas.flatMap(aa => aa.measures || []);
            if (allMeasures.length === 0) return false; // No measures = not completed
            
            return checkpoint.actionAreas.every(aa => {
                if (!aa.measures || aa.measures.length === 0) return true; // Action area without measures
                return aa.measures.every(m => m.status && m.status.trim() !== '' && m.status !== 'nicht begonnen');
            });
        }
        
        if (checkpoint.checkpointType === 'actionareas') {
            // Action areas: completed if ALL action areas have a status other than 'Kein Status'
            // If no action areas exist yet, checkpoint cannot be completed
            if (!checkpoint.actionAreas || checkpoint.actionAreas.length === 0) return false;
            return checkpoint.actionAreas.every(aa => aa.status && aa.status !== '' && aa.status !== 'Kein Status');
        }
        
        return false;
    };
    
    // If completed, always show as completed regardless of date
    if (isCompleted()) {
        return 'completed';
    }
    
    // If past due date, it's overdue
    if (dueDate < today) {
        return 'overdue';
    }
    
    // Otherwise, it's upcoming
    return 'upcoming';
};

/**
 * Returns display text for a status category
 */
const getStatusLabel = (status) => {
    switch (status) {
        case 'overdue':
            return 'Überfällig';
        case 'upcoming':
            return 'Anstehend';
        case 'completed':
            return 'Abgeschlossen';
        default:
            return '';
    }
};

/**
 * Returns the group header message based on status and count
 */
const getGroupHeaderMessage = (status, count, earliestDueDate) => {
    switch (status) {
        case 'overdue':
            return `${count} ${count === 1 ? 'Checkpoint ist' : 'Checkpoints sind'} überfällig!`;
        case 'upcoming':
            return `${count} ${count === 1 ? 'Checkpoint' : 'Checkpoints'}`;
        case 'completed':
            return `${count} ${count === 1 ? 'Checkpoint' : 'Checkpoints'} abgeschlossen`;
        default:
            return '';
    }
};

/**
 * Get the description based on checkpoint type
 */
const getCheckpointDescription = (checkpointType) => {
    switch (checkpointType) {
        case 'measures':
            return 'Bitte bewerte hier den aktuellen Stand der Maßnahmenumsetzung für alle Maßnahmen in Deinem Verantwortungsbereich.';
        case 'actionareas':
            return 'Bitte bewerte hier den endgültigen Status der Entwicklung in Deinen Handlungsfeldern.';
        case 'questions':
            return 'Bitte beantworte die folgende Frage zu Deinem Verantwortungsbereich.';
        default:
            return 'Bitte bewerte den aktuellen Stand in Deinem Verantwortungsbereich.';
    }
};

const getCheckpointCardKey = (checkpointId, displayUnitId) => {
    if (displayUnitId !== null && displayUnitId !== undefined) {
        return `${checkpointId}-${displayUnitId}`;
    }

    return String(checkpointId);
};

const Checkpoints = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const preselectedUnitId = searchParams.get('unitId');
    const preselectedCheckpointId = searchParams.get('checkpointId');
    const handledDeepLinkKeyRef = useRef(null);

    // Select unitId and units from unitsSlice
    const unitId = useSelector(selectUnitId);
    const selectedUnitIds = useSelector(selectSelectedUnitIds);
    const units = useSelector(selectUnits);
    const unitsLedByUser = useSelector(selectUnitsLedByUser);
    const unitsStatus = useSelector((state) => state.units.status);
    const unitsError = useSelector((state) => state.units.error);

    // Get current user data
    const userData = useSelector(selectUserData);
    
    // Build a map to check leadership for each unit
    // Use string keys to ensure consistent lookup regardless of ID type
    const unitLeaderMap = useMemo(() => {
        const map = {};
        if (units && userData) {
            units.forEach((unit) => {
                map[String(unit.id)] = unit.unit_leaders?.includes(userData.id) || false;
            });
        }
        return map;
    }, [units, userData]);
    
    // Check if user is leader of at least one selected unit
    const isLeaderOfAnySelectedUnit = useMemo(() => {
        return selectedUnitIds.some(id => unitLeaderMap[String(id)]);
    }, [selectedUnitIds, unitLeaderMap]);

    // Get the units that are actually selectable (visible in frontend)
    const selectableUnits = useMemo(() => {
        return unitsLedByUser.filter(u => u.visibility_level === 1 || u.visibility_level === 2);
    }, [unitsLedByUser]);

    // Check if there's only one selectable unit
    const hasSingleUnit = selectableUnits.length === 1;
    const singleUnitName = hasSingleUnit ? selectableUnits[0]?.unit_name : null;

    // Select checkpoints state from checkpointsSlice
    const { checkpoints, status: checkpointsStatus, error: checkpointsError } = useSelector(
        (state) => state.checkpoints
    );

    // Track expanded state as "checkpointId-unitId" to allow independent expansion per unit
    const [expandedCheckpointKey, setExpandedCheckpointKey] = useState(null);
    
    // State to hold the "frozen" grouped checkpoints for display
    // This only updates when no accordion is expanded, not during live edits
    const [displayedGroupedCheckpoints, setDisplayedGroupedCheckpoints] = useState(null);

    useEffect(() => {
        document.title = 'TRAC - Checkpoints';
    }, []);

    // Handle preselected unit from URL query params
    useEffect(() => {
        if (preselectedUnitId && unitsStatus === 'succeeded' && units.length > 0) {
            const preselectedUnit = units.find(unit => String(unit.id) === String(preselectedUnitId));
            if (preselectedUnit && String(unitId) !== String(preselectedUnitId)) {
                dispatch(setUnitId(preselectedUnitId));
            }
        }
    }, [preselectedUnitId, unitsStatus, units, unitId, dispatch]);

    // Fetch checkpoints once units are fetched and unitId is available
    useEffect(() => {
        if (unitsStatus === 'succeeded' && unitId && checkpointsStatus === 'idle') {
            dispatch(fetchCheckpoints());
        }
    }, [unitsStatus, unitId, checkpointsStatus, dispatch]);

    // Helper function to deep clone a checkpoint (to prevent frozen state from being mutated)
    const deepCloneCheckpoint = useCallback((checkpoint) => {
        return {
            ...checkpoint,
            actionAreas: checkpoint.actionAreas?.map(aa => ({
                ...aa,
                measures: aa.measures?.map(m => ({ ...m })) || []
            })) || [],
            questions: checkpoint.questions?.map(q => ({ ...q })) || []
        };
    }, []);

    // Helper function to filter checkpoint items by a specific unit
    // For measures and actionareas types, we always return the checkpoint (even if empty)
    // so that users can see checkpoints are scheduled even if no items are defined yet
    const filterCheckpointByUnit = useCallback((checkpoint, targetUnitId) => {
        const filtered = deepCloneCheckpoint(checkpoint);
        
        if (checkpoint.checkpointType === 'questions') {
            filtered.questions = checkpoint.questions?.filter(q => q.unit === targetUnitId).map(q => ({ ...q })) || [];
            // For questions, we still need at least one question to show the checkpoint
            // (questions are created per unit, so if none exist, the checkpoint wasn't set up for this unit)
            if (filtered.questions.length === 0) return null;
        } else if (checkpoint.checkpointType === 'measures') {
            filtered.actionAreas = checkpoint.actionAreas?.map(aa => ({
                ...aa,
                measures: aa.measures?.filter(m => m.unit === targetUnitId).map(m => ({ ...m })) || []
            })).filter(aa => aa.measures.length > 0) || [];
            // For measures, we show the checkpoint even if no measures exist yet
            // The checkpoint is still relevant - user just needs to add measures
        } else if (checkpoint.checkpointType === 'actionareas') {
            filtered.actionAreas = checkpoint.actionAreas?.filter(aa => aa.unit === targetUnitId).map(aa => ({ ...aa })) || [];
            // For actionareas, we show the checkpoint even if no action areas exist yet
            // The checkpoint is still relevant - user just needs to add action areas
        }
        
        // Store the target unit for this filtered checkpoint
        filtered._displayUnitId = targetUnitId;
        
        return filtered;
    }, [deepCloneCheckpoint]);

    // Group checkpoints by status AND unit - this structure is what gets frozen
    // This ensures unit grouping is also frozen along with status grouping
    const currentGroupedCheckpoints = useMemo(() => {
        const groups = {
            overdue: { checkpoints: [], byUnit: {} },
            upcoming: { checkpoints: [], byUnit: {} },
            completed: { checkpoints: [], byUnit: {} }
        };

        // Process each checkpoint
        checkpoints.forEach(checkpoint => {
            // Collect all unit IDs this checkpoint belongs to
            const unitIds = new Set();
            
            if (checkpoint.checkpointType === 'questions' && checkpoint.questions) {
                // For questions, only include units that have questions assigned
                checkpoint.questions.forEach(q => {
                    if (q.unit) unitIds.add(q.unit);
                });
            } else if (checkpoint.checkpointType === 'measures') {
                // For measures checkpoints, include ALL selected units
                // This ensures the checkpoint shows even if no measures exist yet
                selectedUnitIds.forEach(uid => unitIds.add(uid));
                
                // Also include units from existing measures (in case they're not in selection)
                if (checkpoint.actionAreas) {
                    checkpoint.actionAreas.forEach(aa => {
                        aa.measures?.forEach(m => {
                            if (m.unit) unitIds.add(m.unit);
                        });
                    });
                }
            } else if (checkpoint.checkpointType === 'actionareas') {
                // For actionareas checkpoints, include ALL selected units
                // This ensures the checkpoint shows even if no action areas exist yet
                selectedUnitIds.forEach(uid => unitIds.add(uid));
                
                // Also include units from existing action areas (in case they're not in selection)
                if (checkpoint.actionAreas) {
                    checkpoint.actionAreas.forEach(aa => {
                        if (aa.unit) unitIds.add(aa.unit);
                    });
                }
            }
            
            // Filter to only include units that are currently selected
            const relevantUnitIds = [...unitIds].filter(uid => selectedUnitIds.includes(uid));
            
            // For each unit, create a filtered copy and determine its status
            relevantUnitIds.forEach(uid => {
                const filteredCheckpoint = filterCheckpointByUnit(checkpoint, uid);
                if (filteredCheckpoint) {
                    // Determine status based on the FILTERED checkpoint (per-unit)
                    const status = getCheckpointStatus(filteredCheckpoint);
                    filteredCheckpoint.statusCategory = status;
                    
                    // Add to the byUnit structure
                    if (!groups[status].byUnit[uid]) {
                        groups[status].byUnit[uid] = [];
                    }
                    groups[status].byUnit[uid].push(filteredCheckpoint);
                }
            });
        });

        // Sort checkpoints within each unit by due date
        Object.keys(groups).forEach(status => {
            Object.keys(groups[status].byUnit).forEach(uid => {
                groups[status].byUnit[uid].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
            });
        });

        return groups;
    }, [checkpoints, filterCheckpointByUnit, selectedUnitIds]);

    // Initialize displayedGroupedCheckpoints on first load
    useEffect(() => {
        if (displayedGroupedCheckpoints === null && checkpoints.length > 0) {
            setDisplayedGroupedCheckpoints(currentGroupedCheckpoints);
        }
    }, [currentGroupedCheckpoints, displayedGroupedCheckpoints, checkpoints.length]);

    // Freeze behavior: 
    // - When a checkpoint is expanded (expandedCheckpointKey !== null): keep displayedGroupedCheckpoints frozen
    // - When no checkpoint is expanded (expandedCheckpointKey === null): always show current state
    // This ensures that when you close an accordion, the UI immediately updates to reflect any status changes
    useEffect(() => {
        if (expandedCheckpointKey === null) {
            // No checkpoint is expanded - always show the current state
            setDisplayedGroupedCheckpoints(currentGroupedCheckpoints);
        }
        // When a checkpoint IS expanded, we intentionally don't update displayedGroupedCheckpoints
        // This freezes the UI until the accordion is closed
    }, [expandedCheckpointKey, currentGroupedCheckpoints]);

    // Use displayed (frozen) checkpoints for rendering, fallback to current if not yet initialized
    const groupedCheckpoints = displayedGroupedCheckpoints || currentGroupedCheckpoints;

    // Keep expanded content live from Redux even while the surrounding groups stay frozen.
    const liveCheckpointLookup = useMemo(() => {
        const lookup = {};

        Object.values(currentGroupedCheckpoints).forEach((group) => {
            Object.entries(group.byUnit || {}).forEach(([groupUnitId, unitCheckpoints]) => {
                unitCheckpoints.forEach((checkpoint) => {
                    const displayUnitId = checkpoint._displayUnitId ?? groupUnitId;
                    lookup[getCheckpointCardKey(checkpoint.id, displayUnitId)] = checkpoint;
                });
            });
        });

        return lookup;
    }, [currentGroupedCheckpoints]);

    // Create a unit name lookup map
    const unitNameLookup = useMemo(() => {
        const map = {};
        units.forEach((unit) => {
            map[unit.id] = unit.unit_name;
        });
        return map;
    }, [units]);

    // Get earliest overdue date for header message
    const earliestOverdueDate = useMemo(() => {
        const overdueByUnit = groupedCheckpoints.overdue?.byUnit || {};
        let earliest = null;
        Object.values(overdueByUnit).forEach(unitCheckpoints => {
            unitCheckpoints.forEach(cp => {
                if (!earliest || new Date(cp.dueDate) < new Date(earliest)) {
                    earliest = cp.dueDate;
                }
            });
        });
        return earliest;
    }, [groupedCheckpoints.overdue]);

    // Get user's first name
    const userName = useMemo(() => {
        if (!userData) return '';
        return userData.first_name || userData.username || '';
    }, [userData]);

    useEffect(() => {
        if (!preselectedCheckpointId) {
            handledDeepLinkKeyRef.current = null;
            return;
        }

        if (unitsStatus !== 'succeeded' || checkpointsStatus !== 'succeeded') {
            return;
        }

        const targetUnitId = preselectedUnitId ?? unitId;
        if (!targetUnitId) {
            return;
        }

        const checkpointsForTargetUnit = [
            ...(currentGroupedCheckpoints.overdue.byUnit?.[targetUnitId] || []),
            ...(currentGroupedCheckpoints.upcoming.byUnit?.[targetUnitId] || []),
            ...(currentGroupedCheckpoints.completed.byUnit?.[targetUnitId] || []),
        ];

        const targetCheckpoint = checkpointsForTargetUnit.find(
            (checkpoint) => String(checkpoint.id) === String(preselectedCheckpointId)
        );

        if (!targetCheckpoint) {
            return;
        }

        const targetKey = getCheckpointCardKey(targetCheckpoint.id, targetUnitId);
        if (handledDeepLinkKeyRef.current === targetKey) {
            return;
        }

        setDisplayedGroupedCheckpoints(currentGroupedCheckpoints);
        setExpandedCheckpointKey(targetKey);
        handledDeepLinkKeyRef.current = targetKey;
    }, [
        checkpointsStatus,
        currentGroupedCheckpoints,
        preselectedCheckpointId,
        preselectedUnitId,
        unitId,
        unitsStatus,
    ]);

    // Handle checkpoint card click - tracks both checkpoint ID and unit ID
    const handleCheckpointClick = useCallback((checkpointId, unitId) => {
        const key = getCheckpointCardKey(checkpointId, unitId);

        setExpandedCheckpointKey((prev) => {
            if (prev && prev !== key) {
                setDisplayedGroupedCheckpoints(currentGroupedCheckpoints);
            }

            return prev === key ? null : key;
        });
    }, [currentGroupedCheckpoints]);

    // Combine loading states
    const isLoading = unitsStatus === 'loading' || checkpointsStatus === 'loading';

    // Render a checkpoint card
    const renderCheckpointCard = (checkpoint, index, groupUnitId = null) => {
        const status = checkpoint.statusCategory;
        const displayUnitId = checkpoint._displayUnitId ?? groupUnitId ?? null;
        const cardKey = getCheckpointCardKey(checkpoint.id || index, displayUnitId);
        const isExpanded = expandedCheckpointKey === cardKey;
        const expandedCheckpoint = liveCheckpointLookup[cardKey] || checkpoint;

        return (
            <div key={cardKey} className="checkpoint-card-wrapper">
                <div 
                    className="checkpoint-card"
                    onClick={() => handleCheckpointClick(checkpoint.id, displayUnitId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleCheckpointClick(checkpoint.id, displayUnitId);
                        }
                    }}
                >
                    <div className={`checkpoint-status-indicator ${status}`} />
                    
                    <div className="checkpoint-card-content">
                        <div className="checkpoint-status-row">
                            <p className="checkpoint-status-label">{getStatusLabel(status)}</p>
                            <span className="checkpoint-due-date-wrapper">
                                <span className="checkpoint-due-date-separator">/</span>
                                <span className={`checkpoint-due-date ${status === 'overdue' ? 'overdue' : ''}`}>
                                    {new Date(checkpoint.dueDate).toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                    })}
                                </span>
                            </span>
                        </div>
                        
                        <div className="checkpoint-title-row">
                            <h3 className="checkpoint-card-title">{checkpoint.title}</h3>
                        </div>
                        
                        <p className="checkpoint-card-description">
                            {getCheckpointDescription(checkpoint.checkpointType)}
                        </p>

                    </div>
                    
                    <div className="checkpoint-card-arrow">
                        <IconChevronRight 
                            size={32} 
                            stroke={1.5} 
                            style={{ 
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                            }}
                        />
                    </div>
                </div>

                {/* Expanded content - directly render checkpoint content */}
                {isExpanded && (
                    <div className="checkpoint-expanded-content">
                        {/* Render measures checkpoint type */}
                        {expandedCheckpoint.checkpointType === 'measures' && (
                            <>
                                {expandedCheckpoint.actionAreas.map((actionArea, actionAreaIndex) => (
                                    <MeasureStatusRow
                                        key={`measure-${expandedCheckpoint.id}-${actionAreaIndex}-${actionArea.id || ''}`}
                                        checkpointIndex={index}
                                        actionAreaIndex={actionAreaIndex}
                                        actionAreaId={actionArea.id}
                                        unitId={actionArea.measures?.[0]?.unit || displayUnitId || unitId}
                                        measures={actionArea.measures}
                                        checkpointId={expandedCheckpoint.id}
                                        hideHeaders={true}
                                        readOnly={!unitLeaderMap[String(actionArea.measures?.[0]?.unit)]}
                                    />
                                ))}
                                {expandedCheckpoint.actionAreas.length === 0 && (
                                    <div className="checkpoint-empty-state">
                                        <p>Noch keine Maßnahmen definiert.</p>
                                        <p className="checkpoint-empty-hint">Erstelle Maßnahmen in Deinen Handlungsfeldern, um den Fortschritt festzuhalten.</p>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Render action areas checkpoint type */}
                        {expandedCheckpoint.checkpointType === 'actionareas' && (
                            <>
                                {expandedCheckpoint.actionAreas.map((actionArea, actionAreaIndex) => (
                                    <ActionAreaStatusRow
                                        key={`actionarea-${expandedCheckpoint.id}-${actionAreaIndex}-${actionArea.id || ''}`}
                                        checkpointIndex={index}
                                        actionAreaIndex={actionAreaIndex}
                                        actionArea={actionArea}
                                        unitId={actionArea.unit || displayUnitId || unitId}
                                        checkpointId={expandedCheckpoint.id}
                                        hideHeaders={true}
                                        readOnly={!unitLeaderMap[String(actionArea.unit)]}
                                        isLast={actionAreaIndex === expandedCheckpoint.actionAreas.length - 1}
                                    />
                                ))}
                                {expandedCheckpoint.actionAreas.length === 0 && (
                                    <div className="checkpoint-empty-state">
                                        <p>Noch keine Handlungsfelder definiert.</p>
                                        <p className="checkpoint-empty-hint">Erstelle Handlungsfelder, um den Fortschritt festzuhalten.</p>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Render questions checkpoint type */}
                        {expandedCheckpoint.checkpointType === 'questions' && (
                            <>
                                {expandedCheckpoint.questions && expandedCheckpoint.questions.map((question, questionIndex) => (
                                    <QuestionStatusRow
                                        key={`question-${expandedCheckpoint.id}-${questionIndex}-${question.id || ''}`}
                                        checkpointIndex={index}
                                        questionIndex={questionIndex}
                                        question={question}
                                        unitId={question.unit || displayUnitId || unitId}
                                        checkpointId={expandedCheckpoint.id}
                                        readOnly={!unitLeaderMap[String(question.unit)]}
                                    />
                                ))}
                                {(!expandedCheckpoint.questions || expandedCheckpoint.questions.length === 0) && (
                                    <div className="checkpoint-empty-state">
                                        <p>Keine Fragen verfügbar.</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Render a checkpoint group using pre-computed byUnit structure
    const renderCheckpointGroup = (status, groupData) => {
        const checkpointsByUnit = groupData?.byUnit || {};
        
        // Calculate total count across all units (unique checkpoints per unit)
        const totalCount = Object.values(checkpointsByUnit).reduce(
            (sum, arr) => sum + arr.length, 0
        );
        
        // If no checkpoints in any unit, don't render this group
        if (totalCount === 0) return null;

        // If only one unit is selected, don't show unit headers
        const showUnitHeaders = selectedUnitIds.length > 1;
        
        // Sort units by their order in selectedUnitIds for stable ordering
        const sortedUnitIds = selectedUnitIds.filter(uid => checkpointsByUnit[uid]?.length > 0);

        return (
            <div className="checkpoint-group" key={status}>
                <div className="checkpoint-group-header">
                    {status === 'overdue' && (
                        <IconAlertTriangle 
                            size={24} 
                            stroke={2} 
                            className="checkpoint-group-header-icon" 
                        />
                    )}
                    <p className="checkpoint-group-header-text">
                        {getGroupHeaderMessage(
                            status, 
                            totalCount,
                            status === 'overdue' ? earliestOverdueDate : null
                        )}
                    </p>
                </div>
                
                {showUnitHeaders ? (
                    // Render checkpoints grouped by unit with headers (in stable order)
                    sortedUnitIds.map((groupUnitId) => {
                        const unitCheckpoints = checkpointsByUnit[groupUnitId] || [];
                        if (unitCheckpoints.length === 0) return null;
                        
                        const unitName = unitNameLookup[groupUnitId] || 'Unbekannte Einheit';
                        
                        return (
                            <div key={groupUnitId} className="checkpoint-unit-group">
                                <div className="checkpoint-unit-header">
                                    <span className="checkpoint-unit-name">{unitName.toUpperCase()}</span>
                                </div>
                                <div className="checkpoint-unit-list">
                                    {unitCheckpoints.map((checkpoint, index) => 
                                        renderCheckpointCard(checkpoint, index, groupUnitId)
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    // Single unit selected - render without unit headers
                    // Get checkpoints for the single selected unit
                    (() => {
                        const singleUnitId = selectedUnitIds[0];
                        const unitCheckpoints = checkpointsByUnit[singleUnitId] || [];
                        return unitCheckpoints.map((checkpoint, index) => 
                            renderCheckpointCard(checkpoint, index)
                        );
                    })()
                )}
            </div>
        );
    };

    return (
        <div className="checkpoints-page flex-grow-1">
            {/* Header greeting */}
            <div className="checkpoints-header">
                <h1 className="checkpoints-greeting">
                    Hallo {userName}, hier sind Deine Checkpoints.
                </h1>
                <p className="checkpoints-subtext">
                    Behalte den Projektplan im Blick und schätze zu den angegebenen Zeitpunkten den aktuellen Prozess und  Maßnahmenstand ein.
                </p>
            </div>

            <hr />

            {/* Unit filter with read-only indicator */}
            <div className="unit-filter-wrapper d-flex align-items-center">
                {hasSingleUnit ? (
                    <div className="single-unit-display">
                        <span className="single-unit-name">{singleUnitName}</span>
                    </div>
                ) : (
                    <UnitFilterContainer 
                        restrictToLedUnits={true} 
                        singleSelect={false}
                        flatMode={true}
                        hideDisabledOptions={true}
                    />
                )}
                
                {selectedUnitIds.length > 0 && !isLeaderOfAnySelectedUnit && (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="read-only-tooltip">
                                Du hast nur Leserechte für die ausgewählten Einheiten, da Du nicht als Einheitsleiter hinterlegt bist.
                            </Tooltip>
                        }
                    >
                        <span className="read-only-indicator">
                            <IconLock size={18} stroke={1.5} />
                        </span>
                    </OverlayTrigger>
                )}
            </div>

            {/* Loading state */}
            {isLoading && (
                <div className="checkpoints-loading">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            )}

            {/* Error states */}
            {!isLoading && unitsStatus === 'failed' && (
                <div className="alert alert-danger">Error: {unitsError}</div>
            )}
            {!isLoading && checkpointsStatus === 'failed' && (
                <div className="alert alert-danger">Error: {checkpointsError}</div>
            )}

            {/* Checkpoint groups - ordered by priority */}
            {!isLoading && checkpointsStatus === 'succeeded' && (
                <>
                    {renderCheckpointGroup('overdue', groupedCheckpoints.overdue)}
                    {renderCheckpointGroup('upcoming', groupedCheckpoints.upcoming)}
                    {renderCheckpointGroup('completed', groupedCheckpoints.completed)}
                    
                    {/* Empty state if no checkpoints at all */}
                    {checkpoints.length === 0 && (
                        <div className="checkpoint-group-empty">
                            <p>Keine Checkpoints vorhanden.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Checkpoints;
